import { print } from 'graphql'

import { ReadSyncableStateStatisticsDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import { usePolling } from 'modules/integrations/hooks/usePolling'

const SYNCABLE_STATE_STATISTICS_POLLING_INTERVAL_MS = 3 * 60 * 1000

export const useSyncableStateStatistics = ({ skip = false }: { skip?: boolean } = {}) => {
  const {
    syncEnabled,
    marketplaceIntegration,
    configuration,
    getDataCollectionSource,
    companyId,
    contextParameters,
    isBookkeeperView,
  } = usePrivateIntegrationsSyncContext()

  const pollingEnabled = configuration.pollingEnabled ?? false

  const dataCollectionSource = getDataCollectionSource()
  const mappedVariables = dataCollectionSource?.mappedVariables
  const queryForSync = dataCollectionSource?.document ? print(dataCollectionSource.document) : null

  const [fetchSyncableStateStatistics, { data, loading }] = useLazyQuery(
    ReadSyncableStateStatisticsDocument
  )

  const graphqlVariablesJson =
    queryForSync && mappedVariables
      ? JSON.stringify({ query: queryForSync, variables: { ...mappedVariables } })
      : undefined
  const serializedContextParameters = JSON.stringify(contextParameters)

  useUnsafeEffect(() => {
    if (skip || !syncEnabled || !marketplaceIntegration || !companyId || !graphqlVariablesJson)
      return

    fetchSyncableStateStatistics({
      variables: {
        origin: configuration.origin,
        parameters: toSyncParameters(contextParameters),
        graphqlVariablesJson,
      },
      context: {
        isBookkeeperView,
      },
    })
  }, [
    skip,
    syncEnabled,
    marketplaceIntegration,
    companyId,
    isBookkeeperView,
    configuration.origin,
    graphqlVariablesJson,
    serializedContextParameters,
    fetchSyncableStateStatistics,
  ])

  // Poll statistics while the sync run is not actively running.
  // This ensures the statistics update after a sync run completes without needing subscriptions.
  usePolling({
    enabled: pollingEnabled && syncEnabled && !skip && !!companyId,
    callback: () => {
      if (!companyId || !marketplaceIntegration) return

      fetchSyncableStateStatistics({
        variables: {
          companyId,
          marketplaceIntegrationId: marketplaceIntegration.id,
          origin: configuration.origin,
          parameters: toSyncParameters(contextParameters),
          graphqlVariablesJson,
        },
        context: {
          isBookkeeperView,
        },
        fetchPolicy: 'network-only',
      })
    },
    intervalMs: SYNCABLE_STATE_STATISTICS_POLLING_INTERVAL_MS,
  })

  const statistics = data?.integrations?.syncableStateStatisticsConnection?.nodes?.[0]

  return { statistics, loading }
}
